import { forwardRef, useMemo } from 'react';
import styled, { ThemeColor, ThemeColors } from 'styled-components';
import includes from 'lodash/includes';

import { getCurrentTheme, EdnaWrapper } from '@providers/index';
import LoadingIcon from '@icons/LoadingIcon/LoadingIcon';
import CheckmarkIcon from '@icons/CheckmarkIcon/CheckmarkIcon';
import FailureIcon from '@icons/FailureIcon/FailureIcon';
import Icon from '../Icon';
import { IconList, IconListType } from '../Icon/constants';

import { ButtonStyles, ButtonProps, ButtonType } from './types';
import { BUTTON_SIZES, BUTTON_STYLES } from './constants';

const ButtonWrapper = styled.button<
  ButtonProps & {
    buttonStyles: ButtonStyles;
  }
>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  object-fit: contain;
  outline: none;
  border-radius: 6px;
  color: ${(props) => props.buttonStyles.contentColor};
  font-size: ${(props) => props.buttonStyles.fontSize};
  background: ${(props) => props.buttonStyles.backgroundColor};
  border: ${(props) =>
    props.buttonStyles.borderColor
      ? `1px solid ${props.buttonStyles.borderColor}`
      : 0};
  box-shadow: ${(props) => props.buttonStyles.boxShadow};
  padding: ${(props) => props.buttonStyles.padding};
  margin: ${(props) => props.buttonStyles.margin};
  min-height: ${(props) => props.buttonStyles.height};
  max-height: ${(props) => props.buttonStyles.height};
  width: ${(props) => props.buttonStyles.width};
  min-width: ${(props) => props.buttonStyles.minWidth};
  pointer-events: ${(props) => props.buttonStyles.pointerEvents};
  cursor: pointer;
  opacity: ${(props) => (props.disabled ? '0.5' : '1')};
  transition: all 150ms ease;
  &:hover {
    background: ${(props) => props.buttonStyles.hoverColor};
  }
  &:active {
    background: ${(props) => props.buttonStyles.activeColor};
    ${(props) =>
      props.buttonStyles.boxShadow &&
      `box-shadow: 0px 2px 0px 0px rgba(0, 0, 0, 0.10) inset;`};
  }
  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.blue500};
    outline-offset: 1px;
  }
`;

const ButtonContent = styled.div<ButtonProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: 'Inter', sans-serif;
  font-size: inherit;
  font-weight: 500;
  font-style: normal;
  font-stretch: normal;
  letter-spacing: normal;
  text-align: center;
  white-space: nowrap;
  color: inherit;
`;

const ButtonIcon = styled(Icon)<{
  margin: string;
}>`
  margin: ${(props) => props.margin};
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const renderButtonContent = ({
  iconSize,
  iconColor,
  children,
  colors,
  completed,
  completedIconColor,
  failed,
  failedIconColor,
  isLoading,
}: ButtonProps & {
  iconSize: string;
  iconColor: ThemeColor;
  colors: ThemeColors;
}) => {
  if (isLoading) {
    return <LoadingIcon color={iconColor} size={iconSize} />;
  }
  if (completed) {
    return (
      <CheckmarkIcon
        color={completedIconColor || colors[iconColor]}
        size={iconSize}
      />
    );
  }
  if (failed) {
    return (
      <FailureIcon
        color={failedIconColor || colors[iconColor]}
        size={iconSize}
      />
    );
  }
  return <ContentWrapper>{children}</ContentWrapper>;
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      btnType = ButtonType.PRIMARY,
      children,
      completed = false,
      completedIconColor,
      disabled = false,
      dataId,
      failed = false,
      failedIconColor,
      isLoading = false,
      mini = false,
      minWidth,
      margin = '0',
      width,
      iconName,
      iconOnly,
      ...props
    }: ButtonProps,
    ref
  ) => {
    const minWidthValue = useMemo(() => {
      if (iconOnly) {
        return mini ? '24px' : '36px';
      }
      if (minWidth) {
        return minWidth;
      }
      if (width) {
        return width;
      }
      if (!mini) {
        return '80px';
      }
      return 'auto';
    }, [width, mini, iconOnly]);

    const { colors } = getCurrentTheme();

    const showIcon =
      iconName &&
      includes(IconList, IconList[iconName]) &&
      !isLoading &&
      !completed &&
      !failed;

    const btnSize = mini ? 'mini' : 'regular';

    const buttonTypeStyles = BUTTON_STYLES[btnType];
    const defaultButtonSizes = BUTTON_SIZES[btnSize];

    // Text buttons have a different padding than regular buttons
    const padding = useMemo(() => {
      if (iconOnly) {
        return '0';
      }

      switch (btnType) {
        case ButtonType.PRIMARY_TEXT:
        case ButtonType.SECONDARY_TEXT:
        case ButtonType.TEXT_DESTRUCTIVE:
          return '8px';
        default:
          return showIcon ? '8px 12px 8px 8px' : defaultButtonSizes.padding;
      }
    }, [iconOnly, showIcon, btnType]);

    const buttonStyles: ButtonStyles = {
      backgroundColor: colors[buttonTypeStyles.background],
      hoverColor: colors[buttonTypeStyles.hover],
      activeColor: colors[buttonTypeStyles.active || buttonTypeStyles.hover],
      contentColor: colors[buttonTypeStyles.content],
      fontSize: defaultButtonSizes.fontSize,
      height: defaultButtonSizes.height,
      padding: padding,
      pointerEvents: disabled || completed || isLoading ? 'none' : 'auto',
      width: width || 'auto',
      minWidth: minWidthValue,
      margin,
      ...(buttonTypeStyles.border && {
        borderColor: colors[buttonTypeStyles.border],
      }),
      ...(buttonTypeStyles.boxShadow && {
        boxShadow: buttonTypeStyles.boxShadow,
      }),
    };

    const { iconSize } = defaultButtonSizes;

    return (
      <EdnaWrapper>
        <ButtonWrapper
          {...props}
          buttonStyles={buttonStyles}
          data-id={dataId}
          disabled={disabled}
          ref={ref}
        >
          <ButtonContent>
            {showIcon && (
              <ButtonIcon
                name={iconName as IconListType}
                width={iconSize}
                height={iconSize}
                margin={iconOnly ? '0' : '0 6px 0 0'}
              />
            )}
            {!iconOnly &&
              renderButtonContent({
                iconSize,
                iconColor: buttonTypeStyles.content,
                children,
                colors,
                completed,
                completedIconColor,
                failed,
                failedIconColor,
                isLoading,
              })}
          </ButtonContent>
        </ButtonWrapper>
      </EdnaWrapper>
    );
  }
);

Button.displayName = 'Button';

export default Button;
